import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import Table from "@components/atoms/Table";
import Filter from "@components/molecules/Filter";
import { PrivateRoutes } from "@constants/urls";
import { usePagination } from "@hooks/usePagination";
import useRouter from "@hooks/useRouter";
import { colors, typo } from "@styles/index";
import { DateUtil } from "@everon-it/core";
import { Pagination } from "antd";
import React from "react";
import styled from "styled-components";
import { useFormik } from "formik";
import { useV2NoticesControllerFetchNotices, type V2NoticeListResponseDto } from "@everon-it/api-client/contents-gw";
import { noticeData, type NoticeInitialValues } from "./Data";

type NoticeTableDataType = {
    displayDateAt: string;
    rowNumber: number;
} & Omit<V2NoticeListResponseDto, "displayStartAt" | "displayEndAt">;

const NoticeManagement: React.FC = () => {
    // Data
    const filter = noticeData.forms;
    const formInitialValues = noticeData.formInitialValues;
    const tableContentsInfoData = noticeData.tableList;

    // Props
    const MAIN_TITLE = "공지사항 관리";
    const TABLE_TITLE = "공지사항 목록";
    const FILTER_GIRD_COLUMN = 3;
    const tableButtonText = "공지사항 등록";

    // Hooks
    const DETAIL_PATH = PrivateRoutes.NoticeDetail;
    const CREATE_PATH = PrivateRoutes.NoticeCreate;

    const { push } = useRouter();
    const { page, pageSize, setPage, handlePageSizeChange, resetPage, getRowNumber, searchParams, setSearchParams } = usePagination({ syncWithUrl: true });

    // URL 파라미터에서 초기값 읽기
    const getInitialValues = (): NoticeInitialValues => ({
        title: searchParams.get("title") ?? formInitialValues.title,
        isVisible: searchParams.get("isVisible") ?? formInitialValues.isVisible,
        startDate: searchParams.get("startDate") ?? formInitialValues.startDate,
        endDate: searchParams.get("endDate") ?? formInitialValues.endDate,
    });

    // Formik
    const formik = useFormik<NoticeInitialValues>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: values => {
            const filteredData: Record<string, string> = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value !== "") {
                    filteredData[key] = String(value);
                }
            });
            setSearchParams(filteredData);
            resetPage();
        },
    });

    // api-client 훅 사용
    const { data = { rows: [] as V2NoticeListResponseDto[], count: 0 } } = useV2NoticesControllerFetchNotices({
        page,
        pageSize,
        title: formik.values.title || undefined,
        isVisible: formik.values.isVisible ? formik.values.isVisible === "true" : undefined,
        startDate: formik.values.startDate || undefined,
        endDate: formik.values.endDate || undefined,
    });

    // 데이터 추출
    const listData = data.rows;
    const totalCount = data.count;

    // 검색 필터 초기화
    const handleFormReset = () => {
        setSearchParams({});
        resetPage();
        formik.resetForm();
    };

    /** 작성 페이지 이동 */
    const handleCreateClick = () => {
        push(CREATE_PATH);
    };

    /** 상세 페이지 이동 uuid */
    const handleDetailClick = (uuid: string) => {
        push(`${DETAIL_PATH}/${uuid}`);
    };

    const noticeTableListData: NoticeTableDataType[] = listData.map((item: V2NoticeListResponseDto, index: number) => {
        const { displayStartAt, displayEndAt, ...rest } = item;
        return {
            ...rest,
            displayDateAt: `${DateUtil.toDateTimeString(displayStartAt)} ~ ${DateUtil.toDateTimeString(displayEndAt)}`,
            rowNumber: getRowNumber(index),
        };
    });

    return (
        <ContentsWrap>
            <MainHeaderTitle>{MAIN_TITLE}</MainHeaderTitle>

            <ContentBox>
                <Filter formik={formik} filterData={filter} girdColumn={FILTER_GIRD_COLUMN} formInitialValues={formInitialValues} handleFormReset={handleFormReset} />
            </ContentBox>

            <ContentBoxWithHeader>
                <TableHeader>
                    <TableTitle>{TABLE_TITLE}</TableTitle>
                    <HeaderButtonWrap>
                        <Button buttonSize="small" text={tableButtonText} onClick={handleCreateClick} />
                    </HeaderButtonWrap>
                </TableHeader>
                <Table<NoticeTableDataType> rowData={noticeTableListData} tableContentsInfoData={tableContentsInfoData} currentPageSize={pageSize} onDetailClick={handleDetailClick} />
                <TableBottomWrap>
                    <ContentsTotal>
                        총<span>{totalCount}</span>
                    </ContentsTotal>
                    <PageNationWrap>
                        <Pagination
                            defaultCurrent={page}
                            defaultPageSize={pageSize}
                            total={totalCount}
                            showSizeChanger
                            locale={{ items_per_page: "개씩 보기" }}
                            current={page}
                            pageSize={pageSize}
                            onShowSizeChange={handlePageSizeChange}
                            onChange={setPage}
                        />
                    </PageNationWrap>
                </TableBottomWrap>
            </ContentBoxWithHeader>
        </ContentsWrap>
    );
};

export default NoticeManagement;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;
const ContentBoxWithHeader = styled.div`
    margin-top: 20px;
    padding: 22px 20px;
    background: ${colors.WHITE};
    border-radius: 16px;
    border: 1px solid ${colors.GRAY9};
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
`;

const HeaderButtonWrap = styled.div`
    display: flex;
    max-height: 32px;
    gap: 8px;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;

const PageNationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
